<template>
  <v-data-table
    :headers="headers"
    :items="filteredProducts"
    multi-sort
    item-key="name"
    :expanded.sync="expanded"
    :single-expand="singleExpand"
    :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
    :options.sync="options"
  >
    <!-- custom headers -->
    <template v-slot:header.percentage_discount>
      <div class="d-flex align-center">
        <span style="display: inline-block">Desconto</span>
        <v-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon small color="cinza-d10" class="pl-1" style="display: table-cell; vertical-align: middle">info</mf-icon>
            </span>
          </template>
          <span
            >A porcentagem de desconto é aplicado sobre o valor final do produto. Se existir uma outra oferta atrelada a esse produto, o desconto definido aqui
            será aplicado sobre o valor com oferta.</span
          >
        </v-tooltip>
      </div>
    </template>

    <!-- group content -->
    <template v-if="type === 'grouped' && !isRevision" v-slot:body.prepend>
      <tr>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td>
          <field-toggle
            v-if="!isRevision && !disabled"
            v-model="group.name"
            :error="(validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].name.$invalid) || group.name === ''"
            :text="true"
            :column="0"
            :row="groupIndex + 9999"
            :tooltip="isDisabledInRules(rules.percentageOfferSales.name)"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.percentageOfferSales.name)"
          />
          <span v-else>{{ group.name }}</span>
        </td>

        <td>
          <field-toggle
            v-if="!isRevision && !disabled"
            v-model="group.percentage_discount"
            percentage
            :limit-value="100"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].percentage_discount.$invalid"
            :column="2"
            :row="groupIndex + 9999"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.percentageOfferSales.percentageDiscount)"
          />
          <span v-else>{{ group.percentage_discount ? `${localeString(group.percentage_discount, 0)}%` : '-' }}</span>
        </td>

        <td>
          <field-toggle
            v-if="!disableMinAndMaxCpf && !isRevision && !disabled"
            v-model="group.minimum_per_client"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].minMaxPerClientValidation.$invalid"
            :column="3"
            :row="groupIndex + 9999"
            :tooltip-text="tooltipMessage ? tooltipMessage : minAndMaxPerClientTooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="disableMinAndMaxCpf || isDisabledInRules(rules.percentageOfferSales.minimumPerClient) || isJaneModulePermission"
            :tooltip="disableMinAndMaxCpf || isDisabledInRules(rules.percentageOfferSales.minimumPerClient) || isJaneModulePermission"
          />
          <span v-else-if="isRevision && !group.minimum_per_client && group.group_rules_exhibition !== 'PUBLIC'" class="empty-value">Vazio</span>
          <span v-else>{{ group.minimum_per_client || '-' }}</span>
        </td>

        <td>
          <field-toggle
            v-if="!disableMinAndMaxCpf && !isRevision && !disabled"
            v-model="group.maximum_per_client"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].minMaxPerClientValidation.$invalid"
            :column="4"
            :row="groupIndex + 9999"
            :tooltip-text="tooltipMessage ? tooltipMessage : minAndMaxPerClientTooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="disableMinAndMaxCpf || isDisabledInRules(rules.percentageOfferSales.maximumPerClient) || isJaneModulePermission"
            :tooltip="disableMinAndMaxCpf || isDisabledInRules(rules.percentageOfferSales.maximumPerClient) || isJaneModulePermission"
          />
          <span v-else-if="isRevision && !group.maximum_per_client && group.group_rules_exhibition !== 'PUBLIC'" class="empty-value">Vazio</span>
          <span v-else>{{ group.maximum_per_client || '-' }}</span>
        </td>

        <td v-if="isIntelligentDistributionEnabled">
          <field-toggle
            v-if="!disabled && !isIntelligentDistributionEditDisabled"
            v-model="group.distribution_limit"
            :error="validations.groupedProducts.$error && validations.groupedProducts.$each[groupIndex].distribution_limit.$invalid"
            :column="5"
            :row="groupIndex + 9999"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="disabled || isDisabledInRules(rules.offerSales.active)"
            :numbers-only="true"
          />
          <span v-else-if="isRevision && !group.distribution_limit" class="empty-value">Sem limite</span>
          <span v-else>{{ group && group.distribution_limit ? localeString(group.distribution_limit, 0) : '-' }}</span>
        </td>

        <td></td>
        <td></td>
      </tr>
    </template>

    <!-- table content -->
    <template v-slot:item="{ item, expand, isExpanded, index }">
      <tr :class="{ repeated: addErrorClass(item.sku) }">
        <td>
          <div v-if="isMultipleEans(item)">
            <v-btn icon @click="expand(!isExpanded)">
              <mf-icon color="marca-1" :icon="isExpanded ? 'indeterminate_check_box' : 'add_box'" small />
            </v-btn>
          </div>
        </td>

        <td>
          <div v-if="type === 'grouped' ? group.group_rules_exhibition === 'PRIVATE' : true" class="img-container d-flex align-center justify-center">
            <img v-if="item.image_url && item.image_url.length" :src="item.image_url" alt="" width="100%" />
            <mf-icon v-else>image_not_supported</mf-icon>
          </div>
        </td>

        <td>
          {{ item.sku }}
        </td>

        <td>
          <div class="py-0" :class="helpers.wrongEAN[getIndex(item.sku)] ? 'invalid-input' : ''">
            <span v-if="item.product && item.product.eans && item.product.eans.length > 1">
              {{ getEansCount(item) }}
            </span>
            <span v-else style="word-break: keep-all">
              {{ item.ean }}
            </span>
          </div>
        </td>

        <td>
          <field-toggle
            v-if="!item.products && !disableIfIsGrouped && !isRevision && !disabled"
            v-model="item.name"
            :error="
              (type === 'ungrouped' && validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].name.$invalid) ||
              item.name === ''
            "
            :text="true"
            :column="0"
            :row="index"
            :tooltip="isDisabledInRules(rules.percentageOfferSales.name)"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.percentageOfferSales.name)"
          />
          <span v-else>{{ item.name }}</span>
        </td>

        <td v-if="type === 'ungrouped'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled"
            v-model="item.percentage_discount"
            percentage
            :limit-value="100"
            :error="validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].percentage_discount.$invalid"
            :column="1"
            :row="index"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.percentageOfferSales.percentageDiscount)"
          />
          <span v-else>{{ item.percentage_discount ? `${localeString(item.percentage_discount, 0)}%` : '-' }}</span>
        </td>

        <td v-else>
          <span>{{ group.percentage_discount ? `${localeString(group.percentage_discount, 0)}%` : '-' }}</span>
        </td>

        <td v-if="type === 'ungrouped'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled"
            v-model="item.minimum_per_client"
            :error="validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].minMaxPerClientValidation.$invalid"
            :column="2"
            :row="index"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.percentageOfferSales.minimumPerClient)"
          />
          <span v-else-if="isRevision && !item.minimum_per_client" class="empty-value">Vazio</span>
          <span v-else>{{ item.minimum_per_client }}</span>
        </td>

        <td v-else>
          <span v-if="isRevision && !group.minimum_per_client && group.group_rules_exhibition !== 'PUBLIC'" class="empty-value">Vazio</span>
          <span v-else>{{ group.minimum_per_client || '-' }}</span>
        </td>

        <td v-if="type === 'ungrouped'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled"
            v-model="item.maximum_per_client"
            :error="validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].minMaxPerClientValidation.$invalid"
            :column="3"
            :row="index"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.percentageOfferSales.maximumPerClient)"
          />
          <span v-else-if="isRevision && !item.maximum_per_client" class="empty-value">Vazio</span>
          <span v-else>{{ item.maximum_per_client }}</span>
        </td>

        <td v-else>
          <span v-if="isRevision && !group.maximum_per_client && group.group_rules_exhibition !== 'PUBLIC'" class="empty-value">Vazio</span>
          <span v-else>{{ group.maximum_per_client || '-' }}</span>
        </td>

        <td v-if="type === 'ungrouped' && isIntelligentDistributionEnabled">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled && !isIntelligentDistributionEditDisabled"
            v-model="item.distribution_limit"
            :error="validations.ungroupedProducts.$error && validations.ungroupedProducts.$each[getIndex(item.sku)].distribution_limit.$invalid"
            :column="4"
            :row="index"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="disabled || isDisabledInRules(rules.percentageOfferSales.active)"
            :numbers-only="true"
          />
          <span v-else-if="isRevision && !item.distribution_limit" class="empty-value">Sem limite</span>
          <span v-else>{{ item.distribution_limit ? localeString(item.distribution_limit, 0) : '-' }}</span>
        </td>

        <td v-else-if="isIntelligentDistributionEnabled">
          <span v-if="isRevision && group && !group.distribution_limit" class="empty-value">Sem limite</span>
          <span v-else>{{ group && group.distribution_limit ? localeString(group.distribution_limit, 0) : '-' }}</span>
        </td>

        <td v-if="isFranchise && isRevision">
          <div class="d-flex align-center">
            <mf-icon :color="getFranchiseeColor(item.franchisee_eans_frequency_percentage || 0)" size="16" fill class="mr-2" icon="radio_button_unchecked" />
            {{ item.franchisee_eans_frequency_percentage || 0 }}%
          </div>
        </td>

        <td>
          <tooltip :disabled="isTooltipChangingStatusDisabled" :message="tooltipMessage">
            <mf-toggle
              v-model="item.active"
              :disabled="isRevision || isDisabledInRules(rules.percentageOfferSales.active) || disabled"
              class="pa-0 ml-2"
              hide-details
            />
          </tooltip>
        </td>

        <td v-if="!isRevision">
          <tooltip :disabled="!isIndustryCampaign" :message="tooltipMessage">
            <v-btn icon :disabled="isRevision || isIndustryCampaign || item.disabled || disabled" @click="removeItem(item)">
              <mf-icon color="cinza-l30">delete</mf-icon>
            </v-btn>
          </tooltip>
        </td>

        <td>
          <mf-tooltip top max-width="200" variant="secondary" :disabled="!Array.isArray(item.units_with_price_1)">
            <template v-slot:activator="{ on, attrs }">
              <mf-icon v-if="showOfferByUnitWarning(item)" color="#F2803F" fill>warning</mf-icon>
            </template>
            <div v-html="getTooltipForOfferByUnit(item)"></div>
          </mf-tooltip>
        </td>
      </tr>
    </template>

    <template v-slot:expanded-item="{ headers, item }">
      <td class="expanded-itens" :colspan="headers.length">
        <v-checkbox
          v-for="ean in toSet(item.product.eans)"
          :key="ean"
          v-model="item.eans"
          :value="ean"
          :label="ean"
          :disabled="isCheckboxDisabled(ean, item.eans) || isRevision || disabled"
          color="marca-1"
          multiple
        />
      </td>
    </template>
  </v-data-table>
</template>

<script>
import { offerSalesTables } from '../../../mixins/offer-sales-tables'
import intelligentDistributionMixin from '../../../mixins/intelligent-distribution'
import { mapState } from 'vuex'

export default {
  name: 'DynamicPricingPercentageDiscountTable',
  components: {
    FieldToggle: () => import('../../FieldToggle.vue'),
    Tooltip: () => import('../../Tooltip.vue'),
  },
  mixins: [offerSalesTables, intelligentDistributionMixin],
  data: () => ({
    expanded: [],
    singleExpand: true,
  }),
  computed: {
    ...mapState({
      units: (state) => state.units,
    }),
    campaignUnits() {
      return this.campaign?.units
    },
    isTooltipChangingStatusDisabled() {
      return this.isIndustryCampaign ? !this.isDisabledInRules(this.rules.percentageOfferSales.active) : true
    },
  },
  methods: {
    showOfferByUnitWarning(product) {
      const uncommonItems = this.campaignUnits?.filter((item) => !product?.units_with_price_1?.includes(item))
      return Boolean(uncommonItems?.length)
    },
    getTooltipForOfferByUnit(product) {
      if (Array.isArray(product?.units_with_price_1)) {
        const hasCommonItems = product?.units_with_price_1?.some((item) => this.campaignUnits?.includes(item))
        const uncommonItems = this.campaignUnits?.filter((item) => !product?.units_with_price_1?.includes(item))
        if (!hasCommonItems) {
          return 'Este produto não possui preço 1 ou tem preço 1 zerado em todas lojas inseridas na oferta.'
        }
        if (uncommonItems.length) {
          return `Este produto não possui preço 1 ou tem preço 1 zerado ${
            uncommonItems.length > 1 ? 'nas seguintes lojas' : 'na seguinte loja'
          }:<br> ${uncommonItems.join('<br>')}`
        }
      }

      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

.invalid-input {
  color: $vermelho-0;

  .field-padding {
    span {
      border: 1px #ff5252 solid !important;
      padding: 0 5px;
    }
  }
}

.expanded-itens {
  padding-left: 19%;
}

::v-deep .repeated {
  background-color: #ffc4c4;
  &:hover {
    background-color: #ffc4c4 !important;
  }
}

::v-deep th,
::v-deep td {
  padding: 0 8px;
}

.img-container {
  width: 35px;
  height: 35px;

  & img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
